// Section « Visioconférence » de la page Compte : connecter SON compte Google
// ou Microsoft.
//
// C'est dans ce compte que les réunions sont créées quand un participant
// accepte une invitation. Le réglage est personnel : chaque membre connecte le
// sien, et personne ne voit celui des autres.
import { useEffect, useState } from 'react';
import { ApiRequestError } from '../api/client';
import { Button, ErrorBanner, Spinner } from '../components/ui';
import { useConnectVisio, useDisconnectVisio, useVisioAccounts, type VisioAccount } from './api';
import './invitations.css';

const errorOf = (cause: unknown) =>
  cause instanceof ApiRequestError ? cause.message : 'Une erreur inattendue est survenue.';

// Statut court posé dans l'adresse au retour de Google ou de Microsoft.
const RETURN_MESSAGES: Record<string, { ok: boolean; text: string }> = {
  connecte: { ok: true, text: 'Compte connecté : les liens de réunion seront créés automatiquement.' },
  refuse: { ok: false, text: 'Vous n’avez pas donné votre accord : le compte n’est pas connecté.' },
  echec: { ok: false, text: 'La connexion a échoué. Réessayez ; si cela persiste, l’application déclarée chez l’outil est à vérifier.' },
  expire: { ok: false, text: 'La demande de connexion a expiré. Recommencez.' },
};

/** Message après le retour de l'outil (`?visio=…`), puis adresse nettoyée. */
function ReturnNotice() {
  const [status] = useState(() => new URLSearchParams(window.location.search).get('visio'));
  useEffect(() => {
    if (!status) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('visio');
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  }, [status]);
  const msg = status ? RETURN_MESSAGES[status] : undefined;
  if (!msg) return null;
  return (
    <p className={msg.ok ? 'account-result is-ok' : 'account-result'} role="status">
      {msg.text}
    </p>
  );
}

function AccountRow({ account }: { account: VisioAccount }) {
  const connect = useConnectVisio();
  const disconnect = useDisconnectVisio();
  const failure = connect.error ?? disconnect.error;
  return (
    <li className="visio-row">
      <div>
        <strong>{account.label}</strong>
        <span className="inv-sub">
          {account.connected
            ? account.accountEmail ?? 'Compte connecté'
            : account.configured
              ? 'Aucun compte connecté'
              : 'Pas encore configuré sur ce serveur : le lien de réunion se colle à la main.'}
        </span>
        {failure ? <ErrorBanner>{errorOf(failure)}</ErrorBanner> : null}
      </div>
      <div className="account-actions">
        <span className={account.connected ? 'account-pill is-on' : 'account-pill'}>
          {account.connected ? 'Connecté' : account.configured ? 'Non connecté' : 'Non configuré'}
        </span>
        {account.connected ? (
          <Button variant="ghost" loading={disconnect.isPending} onClick={() => disconnect.mutate(account.provider)}>
            Déconnecter
          </Button>
        ) : account.configured ? (
          <Button
            variant="accent"
            loading={connect.isPending}
            // C'est le navigateur entier qui part donner son accord chez l'outil.
            onClick={() => connect.mutate(account.provider, { onSuccess: ({ url }) => window.location.assign(url) })}
          >
            Connecter
          </Button>
        ) : null}
      </div>
    </li>
  );
}

export function VisioAccountsSection() {
  const accounts = useVisioAccounts();
  return (
    <section className="zy-card account-section" aria-labelledby="visio-title">
      <div className="account-head">
        <div>
          <h2 className="t-h3" id="visio-title">Visioconférence</h2>
          <p className="account-lede">
            Connectez votre compte Google ou Microsoft : quand un participant accepte une invitation, le lien de la
            réunion est créé dans ce compte. Ce réglage est personnel — chaque membre connecte le sien.
          </p>
        </div>
      </div>
      <ReturnNotice />
      {accounts.isPending ? (
        <Spinner label="Chargement…" />
      ) : accounts.isError ? (
        <ErrorBanner>{errorOf(accounts.error)}</ErrorBanner>
      ) : (
        <>
          <ul className="visio-list">
            {accounts.data.providers.map((a) => (
              <AccountRow key={a.provider} account={a} />
            ))}
          </ul>
          {accounts.data.demo ? (
            <p className="account-meta">
              Démonstration : la connexion est simulée, et les liens créés n’ouvrent aucune réunion.
            </p>
          ) : null}
        </>
      )}
      <ul className="account-notes">
        <li>Zygosight ne demande que le droit de créer des réunions : ni votre agenda, ni vos e-mails, ni vos fichiers.</li>
        <li>Vous pouvez déconnecter le compte à tout moment ; les liens déjà créés restent valables.</li>
      </ul>
    </section>
  );
}
